import { DateRangeControls } from '@/features/quality/components/DateRangeControls';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { s } from '@/theme/tokens';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Box, Text } from '@/ui/primitives';
import type { QualityRange } from '@/features/quality/hooks';
import type { ReactElement, ReactNode } from 'react';

type SearchTagsDateHeaderFiltersProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchAriaLabel: string;
  selectedTags: string[];
  onTagsChange: (nextTags: string[]) => void;
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  selectedProjects?: string[];
  onProjectsChange?: (nextProjects: string[]) => void;
  projectOptions?: Array<{ value: string; label: string }>;
  selectedStatuses?: string[];
  onStatusesChange?: (nextStatuses: string[]) => void;
  statusOptions?: Array<{ value: string; label: string }>;
  range: QualityRange;
  onRangeChange: (nextRange: QualityRange) => void;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  hasCustomDates: boolean;
  isLatestMode: boolean;
  onSelectedStartDayChange: (nextDay: string | null) => void;
  onSelectedEndDayChange: (nextDay: string | null) => void;
  includeLatest?: boolean;
  actions?: ReactNode;
};

export const SearchTagsDateHeaderFilters = ({
  searchValue,
  onSearchChange,
  searchAriaLabel,
  selectedTags,
  onTagsChange,
  tagOptions,
  tagOptionsLoading,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  range,
  onRangeChange,
  pickerStartDay,
  pickerEndDay,
  hasCustomDates,
  isLatestMode,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  includeLatest = true,
  actions,
}: SearchTagsDateHeaderFiltersProps): ReactElement => {
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: { xs: 'wrap', lg: 'nowrap' },
        gap: { xs: s('md'), lg: s('md') },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: s('md'),
          minWidth: 0,
          flex: { xs: '1 1 100%', md: '1 1 220px', lg: '1 1 240px' },
        }}
      >
        <Text role="label" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
          Search
        </Text>
        <Box
          sx={{
            width: { xs: fixedFilterWidth, md: 'auto' },
            minWidth: { xs: fixedFilterWidth, md: 160, lg: 180 },
            maxWidth: { xs: fixedFilterWidth, md: 'none' },
            flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 auto' },
          }}
        >
          <SearchFilterField
            value={searchValue}
            inputAriaLabel={searchAriaLabel}
            compact
            fullWidth
            controlHeight={32}
            onChange={(event) => {
              onSearchChange(event.target.value);
            }}
            sx={{ width: '100%' }}
          />
        </Box>
      </Box>
      {selectedStatuses && onStatusesChange && statusOptions ? (
        <Box
          sx={{
            width: { xs: fixedFilterWidth, md: 'auto' },
            minWidth: { xs: fixedFilterWidth, md: 160, lg: 180 },
            maxWidth: { xs: fixedFilterWidth, md: 'none' },
            flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 180px', lg: '1 1 200px' },
          }}
        >
          <TagsFilterField
            label={getFilterLabel('status')}
            inputAriaLabel="Filter by status"
            value={selectedStatuses}
            options={statusOptions}
            onChange={onStatusesChange}
            emptyLabel={getFilterEmptyLabel('allStatuses')}
            fullWidth
          />
        </Box>
      ) : null}
      <Box
        sx={{
          width: { xs: fixedFilterWidth, md: 'auto' },
          minWidth: { xs: fixedFilterWidth, md: 160, lg: 180 },
          maxWidth: { xs: fixedFilterWidth, md: 'none' },
          flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 180px', lg: '1 1 200px' },
        }}
      >
        <TagsFilterField
          inputAriaLabel="Filter by tags"
          value={selectedTags}
          options={tagOptions}
          onChange={onTagsChange}
          emptyLabel={
            tagOptionsLoading ? getFilterEmptyLabel('loadingTags') : getFilterEmptyLabel('allTags')
          }
          fullWidth
        />
      </Box>
      {selectedProjects !== undefined && onProjectsChange && projectOptions ? (
        <Box
          sx={{
            width: { xs: fixedFilterWidth, md: 'auto' },
            minWidth: { xs: fixedFilterWidth, md: 160, lg: 180 },
            maxWidth: { xs: fixedFilterWidth, md: 'none' },
            flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 180px', lg: '1 1 200px' },
          }}
        >
          <TagsFilterField
            label={getFilterLabel('project')}
            inputAriaLabel="Filter by project"
            value={selectedProjects}
            options={projectOptions}
            onChange={onProjectsChange}
            emptyLabel={getFilterEmptyLabel('allProjects')}
            fullWidth
          />
        </Box>
      ) : null}
      <Box
        sx={{
          minWidth: 0,
          marginLeft: { lg: 'auto' },
          flex: { xs: '1 1 100%', lg: '0 0 auto' },
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'space-between', lg: 'flex-end' },
          gap: s('md'),
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <DateRangeControls
          value={range}
          onChange={onRangeChange}
          includeLatest={includeLatest}
          fullWidthOnMobile
          displayStartDay={pickerStartDay}
          displayEndDay={pickerEndDay}
          isCustomRange={hasCustomDates && !isLatestMode}
          onSelectedStartDayChange={onSelectedStartDayChange}
          onSelectedEndDayChange={onSelectedEndDayChange}
          showDatePickers
          showQuickFilters
          buttonSx={{
            minHeight: 32,
            height: 32,
            paddingY: s('xxs'),
            paddingX: s('md'),
            lineHeight: 1,
          }}
        />
        {actions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{actions}</Box>
        ) : null}
      </Box>
    </Box>
  );
};

export type { SearchTagsDateHeaderFiltersProps };
